// src/store/projectStore.ts
/**
 * .tuMetrics {"tu1": 0.9, "tu2": 0.92, "tu3": 5, "notes": "Projects load full company set; views filter with TeamFilterChip", "tuVersion": "2.5"}
 * .help "Loads and syncs company projects. Browse team filters live on each view, not in this store."
 */
import { create } from 'zustand';
import { Project, ProjectStatus } from '../types';
import useUserStore from './userStore';
import useSprintStore from './sprintStore'; // November 3, 2025: Import for refreshing sprints
import useRequirementStore from './requirementStore'; // November 3, 2025: Import for refreshing requirements
import useCardStore from './cardStore'; // November 3, 2025: Import for refreshing cards after project linking
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import {
  mapProjectFromDB,
  fetchProjectsWithStats,
  fetchProjectStats,
  fetchProjectProgress,
  insertProject,
  updateProjectById,
  deleteProjectById,
  isProjectKeyUnique,
} from '@/lib/supabase/queries/project-queries';

interface ProjectState {
  projects: Project[];          // Filtered by team selector
  _allProjects: Project[];      // ALL projects (unfiltered) - for lookups like getProjectKey
  currentProjectId: string | null;
  isLoading: boolean;
  error: string | null;
  selectedTeamId: string | null;
  lastFetchedAt: number | null;
  realtimeChannel: RealtimeChannel | null;

  initializeProjects: () => Promise<void>;
  fetchAllProjects: () => Promise<Project[]>; // Fetch ALL projects (no team filter) for dropdowns
  refreshProjects: () => Promise<void>;
  refreshProjectsIfStale: () => Promise<void>;
  setupRealtimeSubscription: () => Promise<void>;
  cleanupRealtimeSubscription: () => void;
  setCurrentProject: (projectId: string | null) => void;
  
  addProject: (data: {
    name: string;
    description: string;
    projectKey: string;
    status?: ProjectStatus;
    teamId?: string;
    roadmapId?: string;
    startDate?: string;
    targetEndDate?: string;
  }) => Promise<Project>;
  
  updateProject: (
    projectId: string,
    updates: Partial<Project>
  ) => Promise<void>;
  
  deleteProject: (projectId: string) => Promise<void>;
  
  checkProjectKeyUnique: (
    projectKey: string,
    excludeProjectId?: string
  ) => Promise<boolean>;
  
  getProjectById: (projectId: string) => Project | undefined;
  getProjectKey: (projectId: string) => string | null;
  
  refreshProjectStats: (projectId: string) => Promise<void>;
}

const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  _allProjects: [],  // Unfiltered projects for lookups
  currentProjectId: null,
  selectedTeamId: null,
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  realtimeChannel: null,

  /**
   * Initialize projects - called on app startup or when navigating to Projects page
   */
  initializeProjects: async () => {
    set({ isLoading: true, error: null });

    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        console.warn('initializeProjects: No current user companyId found');
        set({ projects: [], isLoading: false });
        return;
      }

      // Always load the full company-visible set. Per-view TeamFilterChip filters in the UI.
      const projects = await fetchProjectsWithStats(currentUser.companyId, {});

      // Auto-activate projects that have in-progress tasks or active sprints
      const { cards } = useCardStore.getState();
      const { sprints } = useSprintStore.getState();
      
      const projectsToActivate: string[] = [];
      for (const project of projects) {
        if (project.status === 'planning') {
          // Check for in-progress tasks
          const hasInProgressTasks = cards.some(
            card => card.projectId === project.id && card.status === 'inProgress'
          );
          
          // Check for active sprints
          const hasActiveSprints = sprints.some(
            sprint => sprint.projectId === project.id && sprint.status === 'active'
          );
          
          if (hasInProgressTasks || hasActiveSprints) {
            projectsToActivate.push(project.id);
          }
        }
      }
      
      // Update projects to active status in background (non-blocking)
      if (projectsToActivate.length > 0) {
        Promise.all(
          projectsToActivate.map(projectId =>
            updateProjectById(projectId, { status: 'active' }, undefined, undefined)
              .catch(err => console.error(`Error auto-activating project ${projectId}:`, err))
          )
        ).then(() => {
          // Refresh projects after activation
          get().refreshProjectsIfStale();
        });
      }

      set({
        projects,
        _allProjects: projects,
        isLoading: false,
        lastFetchedAt: Date.now(),
      });
    } catch (error: any) {
      console.error('Error initializing projects:', error);
      set({
        error: error.message || 'Failed to load projects',
        isLoading: false,
      });
    }
  },

  /**
   * Fetch ALL projects without team filtering (for dropdowns/selectors)
   * This should be used in modals/dropdowns where team filtering doesn't apply
   * Also updates _allProjects cache
   */
  fetchAllProjects: async () => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      console.warn('fetchAllProjects: No current user companyId found');
      return [];
    }

    try {
      // Fetch ALL projects (no team filter) - for dropdowns/selectors
      const allProjects = await fetchProjectsWithStats(currentUser.companyId, {
        // No teamId - fetch all projects
      });

      // Update the cache for lookups
      set({ _allProjects: allProjects });

      return allProjects;
    } catch (error: any) {
      console.error('Error fetching all projects:', error);
      return [];
    }
  },

  /**
   * Refresh projects (force re-fetch)
   */
  refreshProjects: async () => {
    await get().initializeProjects();
  },

  /**
   * Refresh projects if data is stale (> 5 minutes old)
   */
  refreshProjectsIfStale: async () => {
    const { lastFetchedAt, projects } = get();
    const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes

    // If no projects loaded yet, initialize
    if (projects.length === 0) {
      await get().initializeProjects();
      return;
    }

    // If data is stale, refresh
    if (!lastFetchedAt || Date.now() - lastFetchedAt > STALE_THRESHOLD) {
      await get().refreshProjects();
    }
  },

  /**
   * Setup realtime subscription for projects
   */
  setupRealtimeSubscription: async () => {
    // Clean up existing subscription
    get().cleanupRealtimeSubscription();

    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      console.warn('setupRealtimeSubscription: No current user companyId found. Skipping realtime subscription setup.');
      return;
    }

    // Ensure projects are loaded first
    const { projects } = get();
    if (projects.length === 0) {
      await get().initializeProjects();
    }

    const channel = supabase
      .channel('projects_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects',
          filter: `company_id=eq.${currentUser.companyId}`,
        },
        async (payload) => {
          console.log('Real-time project payload received:', payload);
          const { eventType, new: newRow, old: oldRow } = payload;

          set((state) => {
            switch (eventType) {
              case 'INSERT': {
                // Map new project from DB
                const newProject = mapProjectFromDB(newRow as any);
                // Fetch stats for the new project; replace in place once enriched
                // (July 2026 fix: this used to prepend a second copy, duplicating
                // every realtime-inserted project in the list)
                Promise.all([
                  fetchProjectStats(newProject.id),
                  fetchProjectProgress(newProject.id),
                ]).then(([stats, progress]) => {
                  const enrichedProject = { ...newProject, stats, progress };
                  set((s) => ({
                    projects: s.projects.map((p) =>
                      p.id === enrichedProject.id ? enrichedProject : p
                    ),
                  }));
                });
                // Return state immediately with basic project (stats will update async)
                // Dedupe: streamStore's agent-object handler may have inserted it already
                if (state.projects.some((p) => p.id === newProject.id)) return state;
                return { projects: [newProject, ...state.projects] };
              }

              case 'UPDATE': {
                const updatedProject = mapProjectFromDB(newRow as any);
                // Refresh stats for updated project
                Promise.all([
                  fetchProjectStats(updatedProject.id),
                  fetchProjectProgress(updatedProject.id),
                ]).then(([stats, progress]) => {
                  const enrichedProject = { ...updatedProject, stats, progress };
                  set((s) => ({
                    projects: s.projects.map((p) =>
                      p.id === enrichedProject.id ? enrichedProject : p
                    ),
                  }));
                });
                // Return state immediately with basic project (stats will update async)
                return {
                  projects: state.projects.map((p) =>
                    p.id === updatedProject.id ? updatedProject : p
                  ),
                };
              }

              case 'DELETE': {
                return {
                  projects: state.projects.filter((p) => p.id !== oldRow.id),
                  currentProjectId:
                    state.currentProjectId === oldRow.id
                      ? null
                      : state.currentProjectId,
                };
              }

              default:
                return state;
            }
          });
        }
      )
      .subscribe();

    set({ realtimeChannel: channel });
    console.log('Projects realtime subscription established');
  },

  /**
   * Cleanup realtime subscription
   */
  cleanupRealtimeSubscription: () => {
    const { realtimeChannel } = get();
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      set({ realtimeChannel: null });
      console.log('Projects realtime subscription cleaned up');
    }
  },

  /**
   * Set the current project ID
   */
  setCurrentProject: (projectId: string | null) => {
    set({ currentProjectId: projectId });
  },

  /**
   * Set the selected team filter
   */

  /**
   * Add a new project
   */
  addProject: async (data) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      throw new Error('No current user company ID found');
    }

    try {
      const newProject = await insertProject(
        {
          name: data.name,
          description: data.description,
          projectKey: data.projectKey,
          status: data.status || 'planning',
          companyId: currentUser.companyId,
          teamId: data.teamId,
          roadmapId: data.roadmapId,
          startDate: data.startDate,
          targetEndDate: data.targetEndDate,
          createdBy: currentUser.id,
        },
        (data as any).sprintIds, // November 3, 2025: Pass sprint IDs for association
        (data as any).featureIds // November 3, 2025: Pass feature IDs for association
      );

      // Fetch stats for the new project
      const [stats, progress] = await Promise.all([
        fetchProjectStats(newProject.id),
        fetchProjectProgress(newProject.id),
      ]);

      const enrichedProject = {
        ...newProject,
        stats,
        progress,
      };

      set((state) => ({
        projects: [enrichedProject, ...state.projects],
      }));

      return enrichedProject;
    } catch (error: any) {
      console.error('Error adding project:', error);
      throw error;
    }
  },

  /**
   * Update an existing project
   * November 3, 2025: Handle sprintIds and featureIds for associations
   */
  updateProject: async (projectId, updates) => {
    try {
      const sprintIds = (updates as any).sprintIds;
      const featureIds = (updates as any).featureIds;
      const cardIds = (updates as any).cardIds;
      
      // Remove sprintIds, featureIds, and cardIds from updates before passing to updateProjectById
      const { sprintIds: _, featureIds: __, cardIds: ___, ...projectUpdates } = updates as any;

      const updatedProject = await updateProjectById(
        projectId,
        projectUpdates,
        sprintIds,
        featureIds,
        cardIds
      );

      // Refresh stats
      const [stats, progress] = await Promise.all([
        fetchProjectStats(projectId),
        fetchProjectProgress(projectId),
      ]);

      const enrichedProject = {
        ...updatedProject,
        stats,
        progress,
      };

      set((state) => ({
        projects: state.projects.map((p) =>
          p.id === projectId ? enrichedProject : p
        ),
      }));

      // November 3, 2025: Refresh sprints and requirements to reflect project associations
      // Also refresh cards so ticket numbers appear immediately
      useSprintStore.getState().refreshSprints();
      useRequirementStore.getState().initializeRequirements();
      useCardStore.getState().initializeCards(); // Refresh cards to show new ticket numbers
    } catch (error: any) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  /**
   * Delete a project
   */
  deleteProject: async (projectId) => {
    try {
      await deleteProjectById(projectId);

      set((state) => ({
        projects: state.projects.filter((p) => p.id !== projectId),
        currentProjectId: state.currentProjectId === projectId ? null : state.currentProjectId,
      }));
    } catch (error: any) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  /**
   * Check if a project key is unique
   */
  checkProjectKeyUnique: async (projectKey, excludeProjectId) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      return false;
    }

    try {
      return await isProjectKeyUnique(currentUser.companyId, projectKey, excludeProjectId);
    } catch (error) {
      console.error('Error checking project key uniqueness:', error);
      return false;
    }
  },

  /**
   * Get a project by ID from the store
   * Uses _allProjects (unfiltered) first so lookups work regardless of team filter
   */
  getProjectById: (projectId) => {
    // First try _allProjects (unfiltered cache)
    const projectFromAll = get()._allProjects.find((p) => p.id === projectId);
    if (projectFromAll) return projectFromAll;
    
    // Fallback to filtered projects
    return get().projects.find((p) => p.id === projectId);
  },

  /**
   * Get a project key by project ID
   * Uses _allProjects (unfiltered) so keys are always available regardless of team filter
   */
  getProjectKey: (projectId) => {
    // First try _allProjects (unfiltered cache)
    const allProjects = get()._allProjects;
    const projectFromAll = allProjects.find((p) => p.id === projectId);
    if (projectFromAll?.projectKey) {
      return projectFromAll.projectKey;
    }
    
    // Fallback to filtered projects (in case _allProjects hasn't loaded yet)
    const project = get().projects.find((p) => p.id === projectId);
    return project?.projectKey || null;
  },

  /**
   * Refresh stats for a specific project
   */
  refreshProjectStats: async (projectId) => {
    try {
      const [stats, progress] = await Promise.all([
        fetchProjectStats(projectId),
        fetchProjectProgress(projectId),
      ]);

      set((state) => ({
        projects: state.projects.map((p) =>
          p.id === projectId
            ? { ...p, stats, progress }
            : p
        ),
      }));
    } catch (error) {
      console.error('Error refreshing project stats:', error);
    }
  },
}));

export default useProjectStore;

